import { 
  array,
  boolean,
  mixed,
  number,
  object,
  string,
  InferType,
} from "yup";
import { int, OneOf, uint } from "../base";
import { bubbleModes, cameraPresets, cameraEases, characterIds, characterMotionTypes, choiceIds, choiceTypes, directions, editedBy, filterPresets, labelColors } from "../consts";
import { CharacterId } from '../../../types' 

export const characterId = OneOf(characterIds);

const characterColors = OneOf(labelColors);

const validCDNUrl = string().test('isValidCDNUrl', (v?: string) => Boolean(v && v.startsWith('https://cms.characters')));
const validAssemblerNUrl = string().test('validAssemblerNUrl', (v?: string) => Boolean(v && v.startsWith('https://sverse-character-storage') && v.includes('assembler')));

const validAnimationUrl = string().test('validAnimationUrl', (v?: string) => validCDNUrl.isValidSync(v) || validAssemblerNUrl.isValidSync(v));

const characterAnimationsTest = {
  name: 'characterAnimationsTest',
  message: "characterAnimationsTest has failed validation", 
  test: (value?: Record<string, string>) => {
    if (!value) {
      return false;
    }
    const testKeys = Object.keys(value).every(k => string().isValidSync(k) && k.startsWith('a_') && k.endsWith('.json'));

    const testValues = Object.values(value).every(v => string().isValidSync(v) && validAnimationUrl.isValidSync(v));

    const isValid = testValues && testKeys;

    if (!isValid) {
      console.error('Error: characterAnimationsTest', {
        testKeys,
        testValues
      })
    }

    return isValid;
  }
};

const characterAnimations = mixed<Record<string, string>>().test(characterAnimationsTest);

export const characterMetadataSchema = object({
  id: string().required(),
  name: string().required(),
  color: characterColors.required(),
  skeleton: string().required(),
  skin: string().required(),
  animations: characterAnimations.required(),
  tokenId: string().required(),
  contractAddress: string().required(),
  attributes: string().default(undefined),
  version: string().default(undefined),
}).noUnknown();

const charactersMetadataTest = {
  name: 'charactersMetadataTest',
  exclusive: false,
  message: "charactersMetadataTest has failed validation", 
  test: function(value?: Record<CharacterId, InferType<typeof characterMetadataSchema> | string>) {
    if (!value) {
      return false;
    }
    
    const keys = Object.keys(value);

    if (keys.length !== 3) {
      return false;
    }

    const testKeys = keys.every(k => string().isValidSync(k) && characterId.isValidSync(k));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const story = (this as any).parent;
    // We need this because testing migration before going to `getDialog` it doesnt replace the metadata url for the schema expect
    const isValidatingMigrator = story?.metadata?.editedBy === 'migrator';
    
    const values = Object.values(value);
  
    const testValues = values.every(v => {
      if (!isValidatingMigrator && typeof v === 'string') {
        return false;
      }

      if (isValidatingMigrator && typeof v === 'string') {
        return string().isValidSync(v);  
      }
      return characterMetadataSchema.isValidSync(v);    
    });

    const isValid = testValues && testKeys;

    if (!isValid) {
      console.error('Error: charactersMetadataTest', {
        testKeys,
        testValues
      })
    }

    return isValid;
  }
};

export const charactersMetadata = mixed<Record<CharacterId, InferType<typeof characterMetadataSchema> | string>>().test(charactersMetadataTest);

export const branchCharacterLayoutSchema = object({
  x: number().required(),
  y: number().required(),
  scale: number().required(),
  zIndex: int.required(),
  dir: number().oneOf(directions).required(),
  layer: int.required(),
}).noUnknown();

export const characterSchema = object({
  id: characterId.required(),
  layout: branchCharacterLayoutSchema.required(),
  disabledOnStart: boolean().default(undefined),
  displayName: string().default(undefined),
}).noUnknown();

const charactersTest = {
  name: 'charactersTest',
  exclusive: false,
  message: "charactersTest has failed validation", 
  test: function(value?: Record<CharacterId, InferType<typeof characterSchema>>) {
    if (!value) {
      return false;
    }
    
    const keys = Object.keys(value);

    if (keys.length !== 3) {
      return false;
    }

    const testKeys = JSON.stringify(keys.sort()) === JSON.stringify(characterIds.sort());
    
    const values = Object.values(value);
  
    const testValues = values.every(v => {
      return characterSchema.isValidSync(v);    
    });

    const isValid = testValues && testKeys;

    if (!isValid) {
      console.error('Error: charactersTest', {
        testKeys,
        testValues
      })
    }

    return isValid;
  }
};

export const characters = mixed<Record<CharacterId, InferType<typeof characterSchema>>>().test(charactersTest);

export const parallaxLayertSchema = object({
  image: string().required(),
  width: number().default(undefined),
  height: number().default(undefined),
  x: number().default(undefined),
  y: number().default(undefined),
}).noUnknown();

export const parallaxtSchema = object({
  width: number().required(),
  height: number().required(),
  y: number().required(),
  layers: array().of(parallaxLayertSchema).required(),
}).noUnknown();

export const navigationSchema = object({
  id: string().required(),
  parent: string().default(undefined),
  previous: string().default(undefined),
  next: string().default(undefined),
}).noUnknown();

const direction = OneOf(directions);

export const stepBubbleLayoutSchema = object({
  x: number().required(),
  y: number().required(),
  dirX: direction.required(),
  dirY: direction.required(),
  tailOffset: number().default(undefined),
}).noUnknown();

export const stepBubbleSchema = object({
  mode: OneOf(bubbleModes).default(undefined),
  tagname: string().default(undefined),
  tagcolor: characterColors.default(undefined),
  charID: characterId.default(undefined),
  layout: stepBubbleLayoutSchema.required(),
  caption: string().required() 
}).noUnknown();

const choiceTypeId = OneOf(choiceIds);

const choiceTypeType = OneOf(choiceTypes);

export const choiceTypeSchema = object({
  id: choiceTypeId.required(),
  type: choiceTypeType.default(undefined),
  caption: string().required(),
  navigation: navigationSchema.required(),
}).noUnknown();

export const choiceTypesSchema = object({
  a: choiceTypeSchema,
  b: choiceTypeSchema.default(undefined),
  c: choiceTypeSchema.default(undefined),
}).noUnknown();

const stepCameraPreset = OneOf(cameraPresets);

export const cameraMotionSchema = object({
  ease: OneOf(cameraEases).default(undefined),
  duration: number().default(undefined),
  zoom: number().default(undefined),
  easeParams: mixed<Record<string, any>>().default(undefined),

  // not be used in current specs but could easily be added:
  // x: number().optional(),
  // y: number().optional(),

  // not sure we want to enable this one, it may break a few things:
  // rotation: number().optional(),
});


export const stepCameraSchema = object({
  charID: characterId.default(undefined),
  preset: stepCameraPreset,
  motions: object({
    // camera motion to the specified preset
    toPreset: cameraMotionSchema.default(undefined),
    // camera motion that starts with the character speech
    alongSpeech: cameraMotionSchema.default(undefined),

    // example of other possible motions:

    // camera motion that loops throughout the character speech
    // throughoutSpeech: cameraMotionSchema.optional(),
    // camera motion that starts when the character speech has ended
    // postSpeech: cameraMotionSchema.optional(),
  }).default(undefined),
}).noUnknown();

export const characterOffsetSchema = object({
  x: number().default(undefined),
  y: number().default(undefined),
  zIndex: number().default(undefined),
}).noUnknown();

const coordinateSchema = object({
  x: number().default(undefined),
  y: number().default(undefined),
}).noUnknown();

export const stepCharacterMotionPositionSchema = object({
  x: number().required(),
  y: number().required(),
}).noUnknown();

export const stepCharacterMotionSchema = object({
  to: stepCharacterMotionPositionSchema.default(undefined),
  speed: number().required(),
  type: OneOf(characterMotionTypes).default(undefined),
}).noUnknown();

export const stepCharacterPositionSchema = coordinateSchema;

export const stepGestureSchema = object({
  charID: characterId.required(),
  dirX: direction.default(undefined),
  gesture: string().default(undefined),
  loop: boolean().default(undefined),
  backToIdle: boolean().default(undefined),
  delay: number().default(undefined),
  charPos: stepCharacterPositionSchema.default(undefined),
  charOffset: characterOffsetSchema.default(undefined),
  charMotion: stepCharacterMotionSchema.default(undefined),
}).noUnknown();

export const dialogStepSchema = object({
  navigation: navigationSchema.required(),
  gestures: array().of(stepGestureSchema).required(),
  parallax: parallaxtSchema.default(undefined),
  camera: stepCameraSchema.default(undefined),
  bubble: stepBubbleSchema.default(undefined),
  choices: choiceTypesSchema.default(undefined),
}).noUnknown();

export const storyMetadataSchema = object({
  timestamp: uint.required(),
  version: uint.required(),
  saveCount: int.required(),
  editedBy: OneOf(editedBy).required(),
  templateIndex: string().default(undefined),
}).noUnknown();

export const steps = array().of(dialogStepSchema);

export const storySchema = object({
  filterPreset: OneOf(filterPresets).required(),
  charactersMetadata: charactersMetadata.required(), // THIS USED TO BE 'characters' IT'S A BREAKING CHANGE!!!
  initialStep: string().required(), // ^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$ (if we want to make the validation more strict to check for uuid v4 format)
  parallax: parallaxtSchema.required(),
  characters: characters.required(),
  steps: steps.required(),
  // Metadata
  /** @deprecated Use 'timestamp' from metadata instead */
  timestamp: uint.required(),
  /** @deprecated Use 'lastVersion' from metadata instead */
  lastVersion: string().test((value?: string) => value === undefined),
  /** @deprecated Use 'version' from metadata instead */
  version: string().required(),
  /** @deprecated Use 'saveCount' from metadata instead */
  saveCount: uint,
  /** @deprecated Use 'editedBy' from metadata instead */
  editedBy: OneOf(editedBy).default(undefined),
  metadata: storyMetadataSchema.default(undefined),
}).noUnknown();

const linkSteps = (steps: InferType<typeof dialogStepSchema>[], initialId: string) => {
  const initialStep = steps.find((step) => step.navigation.id === initialId);
  if (!initialStep) {
    throw Error(`Error: Cannot find 'initialStep' in 'linkSteps()'`);
  }

  // Start iterating from the second step since first step doesn't have 'previous'
  let nextStep = steps.find((step) => step.navigation.id === initialStep.navigation.next);
  let previousStep: InferType<typeof dialogStepSchema> = initialStep;
  while (nextStep) {
    // set the step previous as the id of the previous step
    nextStep.navigation.previous = previousStep.navigation.id;
    // move cursor to next step until we cant find any more next
    previousStep = nextStep;
    nextStep = steps.find((step) => step.navigation.id === nextStep!.navigation.next);
  }

  return steps;
};

export const testStepsNavigation =  (initialStep: string) => { 
  return steps.test({
    name: 'testNavigationLink',
    exclusive: false,
    message: "testNavigationLink has failed validation", 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    test: function(value?: any) {
  
      const steps = value as InferType<typeof dialogStepSchema>[];
  
      let storylineIds: (string | undefined)[] = [undefined];
  
      const choiceStep = steps.find(step => step.choices);
  
      if (choiceStep) {
        const parentIds: (string | undefined)[] = Object.values(choiceStep.choices!).map((v) => v?.navigation.parent);
        storylineIds = storylineIds.concat(parentIds);
      }
  
      const linkedSteps: InferType<typeof dialogStepSchema>[] = [];
  
      storylineIds.forEach((storyline) => {
        const storylineSteps = steps.filter((step) => step.navigation.parent === storyline);
    
        const initialId = storyline ? storyline.split('/')[1] : initialStep;
    
        linkedSteps.push(...linkSteps(storylineSteps, initialId));
      });

      const isValid = linkedSteps.length === steps.length;

      if (!isValid) {
        console.error(`Failed to validate steps navigation`, {
          initialStep,
          storylineIds,
          steps,
          linkedSteps,
        });
      }
  
      return isValid;
    }
  });
}

export type Characters = InferType<typeof characters>;
export type CharacterMotionPosition = InferType<typeof stepCharacterMotionPositionSchema>;
export type CharacterPosition = InferType<typeof stepCharacterPositionSchema>;
export type DialogCharacter = InferType<typeof characterSchema>;
export type DialogCharacterLayout = InferType<typeof branchCharacterLayoutSchema>;
export type DialogCharacterMotion = InferType<typeof stepCharacterMotionSchema>;
export type DialogCharacterOffset = InferType<typeof characterOffsetSchema>;
export type DialogCharacterPosition = InferType<typeof stepCharacterPositionSchema>;
export type DialogCharacterTemplate = InferType<typeof characterMetadataSchema>;
export type DialogChoiceType = InferType<typeof choiceTypeSchema>;
export type DialogChoiceTypes = InferType<typeof choiceTypesSchema>;

export type DialogModule = InferType<typeof storySchema>;

export type DialogStep = InferType<typeof dialogStepSchema>;
export type DialogStepBubble = InferType<typeof stepBubbleSchema>;
export type DialogStepBubbleLayout = InferType<typeof stepBubbleLayoutSchema>;
export type CameraMotion = InferType<typeof cameraMotionSchema>;
export type DialogStepCamera = InferType<typeof stepCameraSchema>;
export type DialogStepGesture = InferType<typeof stepGestureSchema>;
export type DialogStepNavigation = InferType<typeof navigationSchema>;
export type ParallaxLayer = InferType<typeof parallaxLayertSchema>;
export type ParallaxPreset = InferType<typeof parallaxtSchema>;